import { ActionContext } from 'vuex';
import axiosAuth from '@/config/axios';
import { API_URL } from '@/config/api';
import { useNotification } from '@/composables/useNotification';
import { ClientsServicesSchedulesState, ScheduleNextMode } from '../types/state';
import { ClientsServicesSchedules } from '../types/clientsServiceSchedules';
import { DefaultResponse } from '@/types/response';
import { defaultClientServices } from '@/modules/client_services/consts';
import { formatDateToString, getDateFromUrl } from '@/utils/date';
import { IRootState } from '@/types/state';
const { notifySuccess, notifyError } = useNotification();

function groupByRecurrencePeriod(items: ClientsServicesSchedules[]): { recurrencePeriod: number; recurrent: boolean; items: ClientsServicesSchedules[] }[] {
    const valid = (items ?? []).filter((item): item is ClientsServicesSchedules => item != null && item.clientService != null);
    const map = new Map<string, ClientsServicesSchedules[]>();
    for (const item of valid) {
        const cs = item.clientService;
        const recurrent = cs?.recurrent ?? true;
        const period = cs?.recurrencePeriod ?? 1;
        const key = `${recurrent}-${period}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(item);
    }
    return Array.from(map.entries()).map(([key, items]) => {
        const [recurrentStr, periodStr] = key.split('-');
        return {
            recurrencePeriod: parseInt(periodStr, 10),
            recurrent: recurrentStr === 'true',
            items,
        };
    });
}

function getNextDateForGroup(group: { recurrencePeriod: number; recurrent: boolean; items: ClientsServicesSchedules[] }): { preScheduleMonth: string; nextDate: Date } {
    const items = group.items ?? [];
    const period = group.recurrencePeriod ?? 1;
    let dateForRecurrence: Date;
    if (items.length > 0) {
        const maxDate = new Date(Math.max(...items.map((i) => new Date(i.date).getTime())));
        dateForRecurrence = new Date(maxDate);
        dateForRecurrence.setMonth(dateForRecurrence.getMonth() + period);
    } else {
        dateForRecurrence = new Date();
    }
    const year = dateForRecurrence.getFullYear();
    const month = String(dateForRecurrence.getMonth() + 1).padStart(2, '0');
    return { preScheduleMonth: `${year}-${month}`, nextDate: dateForRecurrence };
}

function getRecurrenceLabel(period: number, recurrent: boolean): string {
    if (!recurrent) return 'Não recorrente';
    if (period === 1) return 'Mensal (1 mês)';
    if (period === 2) return 'Bimestral (2 meses)';
    return `${period} meses`;
}

const defaultClientsServicesSchedules = {
    id: '',
    createdAt: new Date(),
    status: 0,
    serviceOrderId: '',
    clientServiceId: '',
    date: new Date(),
    clientService: defaultClientServices,
    isFirst: false,
    itemsSelected: [],
}

const state: () => ClientsServicesSchedulesState = () => ({
    servicesSchedules: [],
    pagination: {
        page: 1,
        itemsPerPage: 10,
        total: 0,
    },
    itemsSelected: [],
    loading: false,
    selectedClientsServicesSchedules: Object.assign({}, defaultClientsServicesSchedules),
    showRescheduleDialog: false,
    showScheduleDialog: false,
    nextExecutionDate: new Date(),
    scheduleMode: 'preSchedule' as ScheduleNextMode,
    preScheduleMonth: '',
    preScheduledList: [],
    preScheduledPagination: { page: 1, itemsPerPage: 10, total: 0 },
    preScheduledLoading: false,
});


const mutations = {

    SET_SERVICES_SCHEDULES(state: ClientsServicesSchedulesState, servicesSchedules: ClientsServicesSchedules[]) {
        state.servicesSchedules = servicesSchedules;
    },

    SET_RESCHEDULE_DIALOG(state: ClientsServicesSchedulesState, payload: { show: boolean }) {
        state.showRescheduleDialog = payload.show
    },
    SET_SCHEDULE_DIALOG(state: ClientsServicesSchedulesState, payload: { show: boolean }) {
        state.showScheduleDialog = payload.show
    },

    SET_NEXT_EXECUTION_DATE(state: ClientsServicesSchedulesState, payload: { date: Date }) {
        state.nextExecutionDate = new Date(payload.date);
    },
    SET_SCHEDULE_MODE(state: ClientsServicesSchedulesState, payload: { mode: ScheduleNextMode }) {
        state.scheduleMode = payload.mode;
    },
    SET_PRE_SCHEDULE_MONTH(state: ClientsServicesSchedulesState, payload: { month: string }) {
        state.preScheduleMonth = payload.month;
    },

    SET_SELECTED_CLIENTS_SERVICES_SCHEDULES(state: ClientsServicesSchedulesState, payload: { selectedClientsServicesSchedules: ClientsServicesSchedules }) {
        state.selectedClientsServicesSchedules = Object.assign({}, payload.selectedClientsServicesSchedules)
        state.selectedClientsServicesSchedules.date = new Date(payload.selectedClientsServicesSchedules.date)
        state.selectedClientsServicesSchedules.date.setDate(state.selectedClientsServicesSchedules.date.getDate() + 2);
    },
    SET_DEFAULT_SELECTED_CLIENTS_SERVICES_SCHEDULES(state: ClientsServicesSchedulesState) {
        state.selectedClientsServicesSchedules = Object.assign({}, defaultClientsServicesSchedules)
    },
    SET_ITEMS_SELECTED(state: ClientsServicesSchedulesState, payload: { itemsSelected: ClientsServicesSchedules[] }) {
        state.itemsSelected = payload.itemsSelected;
    },
    SET_LOADING(state: ClientsServicesSchedulesState, payload: { loading: boolean }) {
        state.loading = payload.loading;
    },
    SET_PRE_SCHEDULED_LIST(state: ClientsServicesSchedulesState, payload: { list: ClientsServicesSchedules[] }) {
        state.preScheduledList = payload.list;
    },
    SET_PRE_SCHEDULED_PAGINATION(state: ClientsServicesSchedulesState, payload: { page?: number; itemsPerPage?: number; total?: number }) {
        if (payload.page !== undefined) state.preScheduledPagination.page = payload.page;
        if (payload.itemsPerPage !== undefined) state.preScheduledPagination.itemsPerPage = payload.itemsPerPage;
        if (payload.total !== undefined) state.preScheduledPagination.total = payload.total;
    },
    SET_PRE_SCHEDULED_LOADING(state: ClientsServicesSchedulesState, payload: { loading: boolean }) {
        state.preScheduledLoading = payload.loading;
    },

};

const getters = {
    finishGroupsByRecurrence(state: ClientsServicesSchedulesState) {
        const groups = groupByRecurrencePeriod(state.itemsSelected);
        return groups.map((g, i) => {
            const { preScheduleMonth, nextDate } = getNextDateForGroup(g);
            return {
                step: i + 1,
                recurrenceLabel: getRecurrenceLabel(g.recurrencePeriod, g.recurrent),
                recurrencePeriod: g.recurrencePeriod,
                items: g.items,
                serviceNames: g.items.map(it => it.clientService?.service?.description ?? '—').filter(Boolean),
                preScheduleMonth,
                nextDate,
            };
        });
    },
};

const actions = {
    openRescheduleDialog({ commit }: ActionContext<ClientsServicesSchedulesState, IRootState>, payload: { selectedClientsServicesSchedules: ClientsServicesSchedules[] }) {
        commit('SET_ITEMS_SELECTED', {itemsSelected: payload.selectedClientsServicesSchedules});
        commit('SET_RESCHEDULE_DIALOG', { show: true });
    },
    closeRescheduleDialog({ commit }: ActionContext<ClientsServicesSchedulesState, IRootState>) {
        commit('SET_DEFAULT_SELECTED_CLIENTS_SERVICES_SCHEDULES');
        commit('SET_RESCHEDULE_DIALOG', { show: false });
    },
    openFinishConfirmationDialog({ commit }: ActionContext<ClientsServicesSchedulesState, IRootState>, payload: ClientsServicesSchedules[] | { selectedClientsServicesSchedules: ClientsServicesSchedules[] }) {
        const items = Array.isArray(payload) ? payload : (payload?.selectedClientsServicesSchedules ?? []);
        commit('SET_ITEMS_SELECTED', { itemsSelected: items });
        commit('SET_SCHEDULE_DIALOG', { show: true });
        commit('SET_SCHEDULE_MODE', { mode: 'preSchedule' });

        let dateForRecurrence = new Date();
        if (items.length > 0) {
            const firstItem = items[0];
            const period = firstItem?.clientService?.recurrencePeriod ?? 1;
            const maxDate = new Date(Math.max(...items.map((i) => new Date(i.date).getTime())));
            dateForRecurrence = new Date(maxDate);
            dateForRecurrence.setMonth(dateForRecurrence.getMonth() + period);
        }
        const year = dateForRecurrence.getFullYear();
        const month = String(dateForRecurrence.getMonth() + 1).padStart(2, '0');
        commit('SET_PRE_SCHEDULE_MONTH', { month: `${year}-${month}` });
        commit('SET_NEXT_EXECUTION_DATE', { date: dateForRecurrence });
    },
    closeScheduleDialog({ commit }: ActionContext<ClientsServicesSchedulesState, IRootState>) {
        commit('SET_DEFAULT_SELECTED_CLIENTS_SERVICES_SCHEDULES');
        commit('SET_NEXT_EXECUTION_DATE', { date: new Date() });
        commit('SET_SCHEDULE_MODE', { mode: 'preSchedule' });
        commit('SET_PRE_SCHEDULE_MONTH', { month: '' });
        commit('SET_SCHEDULE_DIALOG', { show: false });
    },


    async rescheduleService({ state, dispatch }: ActionContext<ClientsServicesSchedulesState, IRootState>) {
        try {
            const ids = state.itemsSelected.map(item => item.id);
            const data = {
                ids,
                date: state.selectedClientsServicesSchedules.date
            }
            await axiosAuth.put(`${API_URL}/clients_services_schedules/reschedule`, data);

            notifySuccess('Serviço reagendado com sucesso');
            dispatch('closeRescheduleDialog');
            dispatch('fetchServicesSchedules');
        } catch (error) {
            notifyError('Erro');
            console.error('Erro ao reagendar serviço:', error);
        }
    },
    async scheduleNextEvent({ commit, state, dispatch }: ActionContext<ClientsServicesSchedulesState, IRootState>) {
        try {
            const ids = state.itemsSelected.map(item => item.id);
            const data = {
                clientServiceIds: ids,
                date: state.nextExecutionDate,
                status: 0,
            };
            await axiosAuth.post(`${API_URL}/clients_services_schedules`, data);
            notifySuccess('Próxima execução agendada com sucesso!');

            commit('SET_SCHEDULE_DIALOG', { show: false });

            dispatch('fetchServicesSchedules');
        } catch (error) {
            console.error('Erro ao agendar próxima execução:', error);
            notifyError('Erro ao agendar próxima execução');
        }
    },
    async finishService({ commit, state, dispatch }: ActionContext<ClientsServicesSchedulesState, IRootState>, payload: { newSchedule: boolean; ids: string[]; closeAfter?: boolean }) {
        try {
            const { ids, newSchedule, closeAfter = true } = payload;
            const lastDate = formatDateToString(new Date());
            const data: Record<string, unknown> = {
                clientServiceScheduleIds: ids,
                lastDate,
                newSchedule,
            };
            if (newSchedule) {
                if (state.scheduleMode === 'preSchedule') {
                    data.preScheduleMonth = state.preScheduleMonth;
                } else {
                    data.nextDate = state.nextExecutionDate;
                }
            }
            await axiosAuth.put(`${API_URL}/clients_services_schedules/finish`, data);
            notifySuccess('Serviço(s) concluído(s) com sucesso');
            if (closeAfter) {
                commit('SET_SCHEDULE_DIALOG', { show: false });
                dispatch('fetchServicesSchedules');
            }
        } catch (error) {
            console.error('Erro ao finalizar o evento:', error);
            notifyError('Erro ao finalizar o evento');
        }
    },

    async fetchServicesSchedules({ commit }: ActionContext<ClientsServicesSchedulesState, IRootState>, payload: { date: string }) {
        try {
            commit('SET_LOADING', { loading: true });
            const date = payload?.date || getDateFromUrl()

            if (!date) {
                notifyError('Data inválida');
                return;
            }
            const response = await axiosAuth.get<DefaultResponse<ClientsServicesSchedules>>(`${API_URL}/clients_services_schedules/day?date=${date}`);
            commit('SET_SERVICES_SCHEDULES', Object.values(response.data.data));
        } catch (error) {
            notifyError('Erro ao buscar serviços agendados');
            console.error('Erro ao buscar serviços agendados:', error);
        } finally {
            commit('SET_LOADING', { loading: false });
        }
    },

    async fetchPreScheduledServices({ commit, state }: ActionContext<ClientsServicesSchedulesState, IRootState>, payload: { year: number; month: number; page?: number; itemsPerPage?: number }) {
        try {
            commit('SET_PRE_SCHEDULED_LOADING', { loading: true });
            const page = payload.page ?? state.preScheduledPagination.page;
            const itemsPerPage = payload.itemsPerPage ?? state.preScheduledPagination.itemsPerPage;
            const params = new URLSearchParams({ page: String(page), itemsPerPage: String(itemsPerPage) });
            const response = await axiosAuth.get<DefaultResponse<ClientsServicesSchedules>>(
                `${API_URL}/clients_services_schedules/pre-schedule/${payload.year}/${payload.month}?${params.toString()}`
            );
            const data = response.data.data ?? [];
            const total = response.data.total ?? data.length;
            commit('SET_PRE_SCHEDULED_LIST', { list: Array.isArray(data) ? data : [] });
            commit('SET_PRE_SCHEDULED_PAGINATION', { page, itemsPerPage, total });
        } catch (error) {
            notifyError('Erro ao buscar serviços pré-agendados');
            console.error('Erro ao buscar serviços pré-agendados:', error);
            commit('SET_PRE_SCHEDULED_LIST', { list: [] });
            commit('SET_PRE_SCHEDULED_PAGINATION', { total: 0 });
        } finally {
            commit('SET_PRE_SCHEDULED_LOADING', { loading: false });
        }
    },

    async schedulePreScheduledItems({ dispatch }: ActionContext<ClientsServicesSchedulesState, IRootState>, payload: { ids: string[]; date: Date; year: number; month: number }) {
        try {
            if (!payload.ids.length) return;
            await axiosAuth.put(`${API_URL}/clients_services_schedules/schedule-pre-scheduled`, {
                ids: payload.ids,
                date: formatDateToString(payload.date),
            });
            notifySuccess(payload.ids.length > 1 ? 'Serviços agendados com sucesso' : 'Serviço agendado com sucesso');
            await dispatch('fetchPreScheduledServices', { year: payload.year, month: payload.month });
        } catch (error) {
            notifyError('Erro ao agendar serviço(s)');
            console.error('Erro ao agendar serviço(s):', error);
        }
    },

};



export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
